<template>
  <Dialog :open="open" @update:open="handleClose" :title="tool?.name ?? 'Tool Details'" size="lg">
    <div v-if="tool" class="space-y-5">
      <div>
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">Description</h4>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {{ tool.description || 'No description' }}
        </p>
      </div>

      <div v-if="tool.input_schema">
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Input Schema</h4>
        <pre
          class="text-xs bg-gray-50 dark:bg-gray-800 rounded p-3 overflow-x-auto max-h-48 border border-gray-200 dark:border-gray-700"
        >{{ JSON.stringify(tool.input_schema, null, 2) }}</pre>
      </div>

      <div v-if="tool.output_schema">
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Output Schema</h4>
        <pre
          class="text-xs bg-gray-50 dark:bg-gray-800 rounded p-3 overflow-x-auto max-h-48 border border-gray-200 dark:border-gray-700"
        >{{ JSON.stringify(tool.output_schema, null, 2) }}</pre>
      </div>

      <div v-if="tool.annotations">
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Annotations</h4>
        <div class="flex flex-wrap gap-2">
          <span
            v-if="tool.annotations.read_only_hint"
            class="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
          >read-only</span>
          <span
            v-if="tool.annotations.destructive_hint"
            class="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
          >destructive</span>
          <span
            v-if="tool.annotations.idempotent_hint"
            class="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
          >idempotent</span>
          <span
            v-if="tool.annotations.open_world_hint"
            class="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
          >open-world</span>
        </div>
      </div>

      <EnvSecretsPanel v-if="showEnvSecrets" :tool-id="tool.id" :org-id="orgId" />

      <div class="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="outline" @click="handleClose">Close</Button>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
  import { Button, Dialog } from '@asabdev/asab-ui';
  import type { BaseTool, CustomTool } from '@/lib/mcp/types';
  import EnvSecretsPanel from './EnvSecretsPanel.vue';

  interface Props {
    open: boolean;
    tool: BaseTool | CustomTool | null;
    orgId: string;
    showEnvSecrets?: boolean;
  }

  interface Emits {
    (e: 'update:open', value: boolean): void;
  }

  defineProps<Props>();
  const emit = defineEmits<Emits>();

  const handleClose = () => emit('update:open', false);
</script>
